import React, { useState, useEffect } from 'react';
import { Image as ImageIcon, Loader2, Grid3x3, Grid2x2, Square, ChevronUp, ChevronDown, Trash2, User, CheckCircle, Sparkles, Palette } from 'lucide-react';
import StarRating from '../shared/StarRating';
import ShareModal from '../modals/ShareModal';
import VehicleLightbox from './VehicleLightbox';
import RemixModal from '../generation/RemixModal';
import { apiClient } from '../../utils/apiClient';
import { snapText } from '../../utils/snapFormatter';  // NEU: SNAP-Formatierung

const VehicleGallery = ({ generations, loading, onDelete, trainingId, onUsePrompt, onRemix, onModify, isGenerating, avatarId }) => {
  // Mobil: Einzelansicht (1), Desktop: Zweier-Ansicht (2)
  const [gridView, setGridView] = useState(
    typeof window !== 'undefined' && window.innerWidth < 640 ? 1 : 2
  );
  const [shareImage, setShareImage] = useState(null);
  const [lightboxImages, setLightboxImages] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [recentDeletes, setRecentDeletes] = useState(new Set());
  const [expandedGeneration, setExpandedGeneration] = useState(null);
  const [remixImage, setRemixImage] = useState(null); // Remix state
  
  // Scroll Detection
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Load ratings from localStorage
  const getRating = (imageId) => {
    const ratings = JSON.parse(localStorage.getItem('snapshot_ratings') || '{}');
    return ratings[imageId] || 0;
  };

  const handleRatingChange = () => {
    setRefreshKey(prev => prev + 1);
  };

  const openLightbox = (images, index) => {
    setLightboxImages(images);
    setLightboxIndex(index);
  };
  
  const closeLightbox = (finalIndex) => {
    // Finde heraus, zu welcher Generation das Bild gehört
    if (finalIndex !== undefined && lightboxImages && lightboxImages[finalIndex]) {
      const currentImage = lightboxImages[finalIndex];
      // Extract generation ID from image ID (format: "gen_id_imageIndex")
      const genId = currentImage.id.split('_')[0];
      
      console.log('🔍 Lightbox closed at image:', currentImage.id, 'Generation:', genId);
      
      // Scroll zur Generation-Card
      setTimeout(() => {
        const genCard = document.querySelector(`[data-generation-id="${genId}"]`);
        if (genCard) {
          genCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
          console.log('✅ Scrolled to generation card:', genId);
        }
      }, 100);
    }
    
    setLightboxImages(null);
  };
  
  // Delete single image (DSGVO compliant with warnings)
  const deleteImage = async (imageId, event, generationId, totalImagesInGen) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Prevent duplicate calls
    if (recentDeletes.has(imageId)) {
      console.log('⚠️ Image recently deleted, ignoring duplicate call:', imageId);
      return;
    }
    
    // WARNUNG: Letztes Bild dieser Generation?
    if (totalImagesInGen === 1) {
      const confirmed = window.confirm(
        '⚠️ Das ist das letzte Bild dieser Generation.\n\n' +
        'Die ganze Generation-Card und der Prompt aus der History werden gelöscht.\n\n' +
        'Wirklich löschen?'
      );
      if (!confirmed) {
        console.log('❌ User cancelled deletion of last image');
        return;
      }
    } else {
      // Normale Warnung
      if (!window.confirm('Bild wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
        console.log('❌ User cancelled deletion');
        return;
      }
    }
    
    setDeleting(imageId);
    setRecentDeletes(prev => new Set([...prev, imageId]));
    
    try {
      const deleteUrl = `/api/vehicles/images/${imageId}`;
      console.log(`🌐 DELETE ${deleteUrl}`);
      
      const response = await apiClient.delete(deleteUrl);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Löschen fehlgeschlagen');
      }
      
      const result = await response.json();
      console.log('✅ Image deleted successfully:', result);
      
      // Notify parent to refresh
      if (onDelete) {
        console.log('📢 Notifying parent component to refresh gallery');
        onDelete(imageId);
      }
      
      // Remove from recentDeletes after 2 seconds
      setTimeout(() => {
        setRecentDeletes(prev => {
          const newSet = new Set(prev);
          newSet.delete(imageId);
          return newSet;
        });
      }, 2000);
      
    } catch (error) {
      console.error('❌ Delete failed:', error);
      alert(`Fehler beim Löschen: ${error.message}`);
      setRecentDeletes(prev => {
        const newSet = new Set(prev);
        newSet.delete(imageId);
        return newSet;
      });
    } finally {
      setDeleting(null);
    }
  };
  
  const scrollToTop = () => {
    const gallerySection = document.querySelector('[data-testid="gallery-section"]');
    if (gallerySection) {
      gallerySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  // Label Mapping (same as PromptHistoryPanel)
  const getLabelForValue = (category, value) => {
    const labels = {
      time_of_day: {
        'sunrise': 'Sonnenaufgang',
        'morning': 'Morgen',
        'noon': 'Mittag',
        'afternoon': 'Nachmittag',
        'sunset': 'Sonnenuntergang',
        'golden_hour': 'Goldene Stunde',
        'blue_hour': 'Blaue Stunde',
        'evening': 'Abend',
        'night': 'Nacht',
        'midnight': 'Mitternacht'
      },
      weather: {
        'clear': 'Klar',
        'cloudy': 'Bewölkt',
        'overcast': 'Bedeckt',
        'foggy': 'Nebelig',
        'rainy': 'Regnerisch',
        'stormy': 'Stürmisch',
        'snowy': 'Schneefall'
      },
      camera: {
        '24mm': '24mm',
        '35mm': '35mm',
        '50mm': '50mm',
        '85mm': '85mm',
        'drone': 'Drohne',
        'top': 'Von oben',
        'low': 'Von unten'
      },
      style: {
        'cinematic': 'Cinematic',
        'action': 'Action',
        'editorial': 'Editorial',
        'vintage': 'Vintage',
        'documentary': 'Doku',
        'fashion': 'Fashion',
        'artistic': 'Künstlerisch'
      },
      substyle: {
        'standard': 'Standard',
        'epic': 'Episch',
        'moody': 'Moody',
        'bright': 'Hell',
        'dramatic': 'Dramatisch'
      },
      image_format: {
        'square_1_1': 'Quadrat 1:1',
        'portrait_9_16': 'Portrait 9:16',
        'portrait_4_5': 'Portrait 4:5',
        'landscape_16_9': 'Landscape 16:9',
        'landscape_3_2': 'Landscape 3:2'
      }
    };
    
    return labels[category]?.[value] || value;
  };

  if (loading) {
    return (
      <div className="bg-black border-2 border-white/10 rounded-2xl p-8 text-center">
        <Loader2 className="w-12 h-12 mx-auto mb-4 text-[#06B6D4] animate-spin" />
        <p className="text-white/70">Lade Galerie...</p>
      </div>
    );
  }

  const gridClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    4: 'grid-cols-2 sm:grid-cols-4'
  }[gridView];

  return (
    <div className="space-y-8">
      
      {generations.length === 0 ? (
        <div className="bg-black border-2 border-white/10 rounded-2xl p-16 text-center">
          <ImageIcon className="w-16 h-16 mx-auto mb-4 text-white/20" />
          <p className="text-white/60 text-lg mb-2">Lade SNAPs auf und leg los! 🚀</p>
          <p className="text-white/40 text-sm">Starte mit &quot;Bild erstellen&quot; oben</p>
        </div>
      ) : (
        /* Instagram-Style: Each Generation is a Card */
        generations.map((gen, genIndex) => {
          const images = gen.image_urls || [gen.image_url];
          
          // Überspringe nur wenn WIRKLICH keine Bilder da sind
          const validImages = images.filter(url => url && url.trim());
          if (validImages.length === 0) {
            return null;
          }
          
          const isExpanded = expandedGeneration === gen.id;
            
            // Parse settings from generation (might be in different formats)
            const settings = gen.generation_settings || gen.settings || {};
            const promptText = gen.prompt_text || gen.prompt || '';
            
            return (
            <div 
              key={gen.id} 
              className="bg-black border-4 border-[#06B6D4]/30 rounded-2xl p-6 space-y-4"
              data-generation-id={gen.id}
            >
              {/* Generation Header with Grid Toggle */}
              <div className="flex items-center justify-between pb-3 border-b border-white/10">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-white font-bold text-lg">Generation #{generations.length - genIndex}</h4>
                    {gen.is_remix && (
                      <div 
                        className="flex items-center gap-1.5 px-2.5 py-1 bg-purple-500/20 border border-purple-500/50 rounded-lg"
                        title="Remix Generation"
                      >
                        <Sparkles className="w-4 h-4 text-purple-400" />
                        <span className="text-purple-300 text-xs font-semibold">Remix</span>
                      </div>
                    )}
                    {gen.is_modification && (
                      <div 
                        className="flex items-center gap-1.5 px-2.5 py-1 bg-orange-500/20 border border-orange-500/50 rounded-lg"
                        title="Modifizierte Generation"
                      >
                        <Palette className="w-4 h-4 text-orange-400" />
                        <span className="text-orange-300 text-xs font-semibold">Modifiziert</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">
                      {new Date(gen.created_at + (String(gen.created_at).includes('Z') || String(gen.created_at).includes('+') ? '' : 'Z')).toLocaleString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                    {/* Kosten-Text direkt darunter */}
                    {gen.cost_credits && (
                      <p className="text-white/50 text-sm">
                        💰 {snapText(gen.cost_credits)} bezahlt
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Grid Toggle Buttons - Desktop: 3 Buttons, Mobile: 2 Buttons */}
                <div className="flex items-center gap-2">
                  {/* Desktop: 3 Buttons (1/2/4) */}
                  <div className="hidden sm:flex items-center gap-2">
                    <button
                      onClick={() => setGridView(1)}
                      className={`p-2 rounded-lg transition-all ${
                        gridView === 1
                          ? 'bg-[#06B6D4] text-black'
                          : 'bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                      title="Einzelansicht"
                    >
                      <Square className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setGridView(2)}
                      className={`p-2 rounded-lg transition-all ${
                        gridView === 2
                          ? 'bg-[#06B6D4] text-black'
                          : 'bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                      title="2 Bilder"
                    >
                      <Grid2x2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setGridView(4)}
                      className={`p-2 rounded-lg transition-all ${
                        gridView === 4
                          ? 'bg-[#06B6D4] text-black'
                          : 'bg-white/5 text-white/60'
                      }`}
                      title="4 Bilder (2x2)"
                    >
                      <Grid3x3 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Mobile: 2 Buttons (1/4) */}
                  <div className="flex sm:hidden items-center gap-2">
                    <button
                      onClick={() => setGridView(1)}
                      className={`p-2 rounded-lg transition-all ${
                        gridView === 1
                          ? 'bg-[#06B6D4] text-black'
                          : 'bg-white/5 text-white/60'
                      }`}
                      title="Einzelansicht"
                    >
                      <Square className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setGridView(4)}
                      className={`p-2 rounded-lg transition-all ${
                        gridView === 4
                          ? 'bg-[#06B6D4] text-black'
                          : 'bg-white/5 text-white/60'
                      }`}
                      title="4 Bilder (2x2)"
                    >
                      <Grid3x3 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Image Grid */}
              <div className={`grid ${gridClass} gap-4`} key={refreshKey}>
                {images.map((url, imgIdx) => {
                  const imageId = `${gen.id}_${imgIdx}`;
                  
                  if (!url) return null;
                  
                  return (
                    <div 
                      key={imageId} 
                      className="relative group overflow-hidden rounded-xl border-2 border-white/10 hover:border-[#06B6D4]/50 transition-colors duration-200"
                    >
                      {/* Bild - komplett frei, ohne Overlays */}
                      <div
                        className="cursor-pointer"
                        onClick={() => {
                          const allImages = generations.flatMap((g) => 
                            (g.image_urls || [g.image_url])
                              .map((imgUrl, idx) => {
                                const imgId = `${g.id}_${idx}`;
                                if (!imgUrl) return null;
                                return {
                                  id: imgId,
                                  url: imgUrl,
                                  prompt: g.prompt_text || g.prompt,
                                  rating: getRating(imgId)
                                };
                              })
                              .filter(Boolean)
                          );
                          const flatIndex = allImages.findIndex(img => img.url === url);
                          openLightbox(allImages, flatIndex);
                        }}
                      >
                        <img 
                          src={url} 
                          alt={`Generated ${imgIdx + 1}`}
                          className="w-full h-auto"
                          onError={(e) => {
                            // iOS Safari Fix: Verstecke broken images
                            e.target.style.display = 'none';
                            if (e.target.parentElement) e.target.parentElement.style.display = 'none';
                          }}
                        />
                      </div>
                      
                      {/* Action Bar unter dem Bild - Responsive Layout */}
                      <div className={`bg-white/5 backdrop-blur-xl border-t border-white/10 ${
                        gridView === 1 
                          ? 'flex items-center justify-between p-3' 
                          : 'p-3 space-y-3'
                      }`}>
                        
                        {/* Rating - zentriert in Grid */}
                        <div onClick={(e) => e.stopPropagation()} className={gridView === 1 ? '' : 'flex justify-center'}>
                          <StarRating imageId={imageId} initialRating={getRating(imageId)} />
                        </div>
                        
                        {/* Action Icons - zentriert in Grid */}
                        <div className={`flex items-center ${gridView === 1 ? 'gap-2' : 'gap-1.5 justify-center'}`}>
                          
                          {/* Remix Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setRemixImage({
                                url: url,
                                imageId: imageId,
                                prompt: promptText,
                                settings: settings,
                                generationId: gen.id
                              });
                            }}
                            className={`bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg hover:bg-white/20 transition-all text-white ${
                              gridView === 1 ? 'p-2' : 'p-1.5'
                            }`}
                            title="Remixen"
                            type="button"
                            data-testid={`remix-${imageId}`}
                          >
                            <Sparkles className={gridView === 1 ? 'w-4 h-4' : 'w-3.5 h-3.5'} />
                          </button>
                          
                          {/* Profile Picture Button */}
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              
                              try {
                                const token = localStorage.getItem('snapshot_token');
                                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/user/profile-image`, {
                                  method: 'POST',
                                  headers: {
                                    'Authorization': `Bearer ${token}`,
                                    'Content-Type': 'application/json'
                                  },
                                  body: JSON.stringify({ image_url: url, avatar_id: avatarId })
                                });
                                
                                if (!res.ok) throw new Error('Fehler beim Speichern');
                                
                                window.dispatchEvent(new CustomEvent('profileImageUpdated', { detail: { imageUrl: url } }));
                                
                                const toast = document.createElement('div');
                                toast.textContent = '✅ Ist jetzt dein Profilbild';
                                toast.className = 'fixed top-20 left-1/2 transform -translate-x-1/2 bg-[#06B6D4] text-black px-6 py-3 rounded-xl font-bold shadow-xl z-50';
                                document.body.appendChild(toast);
                                setTimeout(() => toast.remove(), 2000);
                              } catch (error) {
                                console.error('Profilbild-Fehler:', error);
                              }
                            }}
                            className={`bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg hover:bg-white/20 transition-all text-white ${
                              gridView === 1 ? 'p-2' : 'p-1.5'
                            }`}
                            title="Profilbild"
                            type="button"
                            data-testid={`set-profile-pic-${imageId}`}
                          >
                            <User className={gridView === 1 ? 'w-4 h-4' : 'w-3.5 h-3.5'} />
                          </button>
                          
                          {/* Delete Button */}
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              deleteImage(imageId, e, gen.id, images.filter(Boolean).length);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                            disabled={deleting === imageId}
                            className={`backdrop-blur-sm border rounded-lg transition-all text-white ${
                              gridView === 1 ? 'p-2' : 'p-1.5'
                            } ${
                              deleting === imageId 
                                ? 'bg-white/20 border-white/30 cursor-not-allowed'
                                : 'bg-red-500/90 border-red-600 hover:bg-red-600'
                            }`}
                            title="Löschen"
                            type="button"
                          >
                            {deleting === imageId ? (
                              <div className={`border-2 border-white border-t-transparent rounded-full animate-spin ${gridView === 1 ? 'w-4 h-4' : 'w-3.5 h-3.5'}`}></div>
                            ) : (
                              <Trash2 className={gridView === 1 ? 'w-4 h-4' : 'w-3.5 h-3.5'} />
                            )}
                          </button>
                          
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Accordion: Prompt Details */}
              <div className="border-t-4 border-[#06B6D4]/30 pt-4">
                <button
                  onClick={() => setExpandedGeneration(isExpanded ? null : gen.id)}
                  className="w-full flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#06B6D4]/20 rounded-xl flex items-center justify-center">
                      <svg className="w-5 h-5 text-[#06B6D4]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <h3 className="text-white font-bold text-base">Prompt Details</h3>
                      <p className="text-white/50 text-xs">Einstellungen anzeigen & übernehmen</p>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-[#06B6D4]" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-white/50" />
                  )}
                </button>
                
                {/* Expanded Content */}
                {isExpanded && (
                  <div className="mt-4 p-6 bg-gradient-to-br from-white/5 to-white/[0.02] border-2 border-[#06B6D4]/30 rounded-xl space-y-4">
                    {/* Prompt Text */}
                    {promptText && (
                      <div>
                        <p className="text-white/50 text-xs mb-2 font-semibold uppercase tracking-wider">Dein Prompt</p>
                        <p className="text-white font-semibold text-base leading-relaxed">{promptText}</p>
                      </div>
                    )}
                    
                    {/* Settings Chips */}
                    {settings && Object.keys(settings).length > 0 && (
                      <div>
                        <p className="text-white/50 text-xs mb-3 font-semibold uppercase tracking-wider">Einstellungen</p>
                        <div className="flex flex-wrap gap-2">
                          {settings.time_of_day && (
                            <span className="px-3 py-1.5 bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg text-white/90 text-xs font-medium hover:bg-white/10 transition-all">
                              {getLabelForValue('time_of_day', settings.time_of_day)}
                            </span>
                          )}
                          {settings.weather && (
                            <span className="px-3 py-1.5 bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg text-white/90 text-xs font-medium hover:bg-white/10 transition-all">
                              {getLabelForValue('weather', settings.weather)}
                            </span>
                          )}
                          {settings.camera && (
                            <span className="px-3 py-1.5 bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg text-white/90 text-xs font-medium hover:bg-white/10 transition-all">
                              {getLabelForValue('camera', settings.camera)}
                            </span>
                          )}
                          {settings.style && (
                            <span className="px-3 py-1.5 bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg text-white/90 text-xs font-medium hover:bg-white/10 transition-all">
                              {getLabelForValue('style', settings.style)}
                            </span>
                          )}
                          {settings.substyle && (
                            <span className="px-3 py-1.5 bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg text-white/90 text-xs font-medium hover:bg-white/10 transition-all">
                              {getLabelForValue('substyle', settings.substyle)}
                            </span>
                          )}
                          {settings.image_format && (
                            <span className="px-3 py-1.5 bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg text-white/90 text-xs font-medium hover:bg-white/10 transition-all">
                              {getLabelForValue('image_format', settings.image_format)}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Übernehmen Button */}
                    {onUsePrompt && (
                      <button
                        onClick={() => {
                          onUsePrompt({
                            user_prompt: promptText,
                            settings: settings
                          });
                          setExpandedGeneration(null); // Close accordion
                        }}
                        className="w-full py-3 px-6 bg-[#06B6D4] hover:bg-[#059669] text-black font-bold text-sm rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#06B6D4]/20"
                      >
                        <CheckCircle className="w-5 h-5" />
                        Prompt übernehmen
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}
      
      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 p-4 bg-[#06B6D4] hover:bg-[#059669] text-black rounded-full shadow-2xl transition-all z-40 animate-bounce"
          title="Nach oben scrollen"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
      )}
      
      {/* Lightbox */}
      {lightboxImages && (
        <VehicleLightbox
          images={lightboxImages}
          initialIndex={lightboxIndex}
          onClose={closeLightbox}
        />
      )}
      
      {/* Share Modal */}
      {shareImage && (
        <ShareModal
          image={shareImage}
          onClose={() => setShareImage(null)}
        />
      )}
      
      
      {/* Remix Modal */}
      {remixImage && (
        <RemixModal
          isOpen={true}
          onClose={() => setRemixImage(null)}
          originalImage={remixImage.url}
          originalPrompt={remixImage.prompt}
          originalSettings={remixImage.settings}
          onRemix={async (remixData) => {
            if (onRemix) {
              await onRemix(remixData, remixImage.imageId);
            }
            // Close modal after generation completes
            setRemixImage(null);
          }}
          isGenerating={isGenerating}
        />
      )}
    </div>
  );
};

export default VehicleGallery;
